import { useEffect, useState, useCallback } from 'react';
import { ChevronRight, ChevronDown, User as UserIcon } from 'lucide-react';
import { api } from '../api/client';
import { useAuth } from '../stores/authStore';
import { TopBar } from '../components/layout/TopBar';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { StatCard } from '../components/ui/StatCard';
import { fmt } from '../lib/format';

interface Node {
  id: string;
  username: string;
  vRank: string;
  referralCode: string;
  createdAt: string;
  childCount: number;
  personalItems: number;
  totalBonusUsdt: string;
}

interface Response { root: Node; children: Node[] }

export function HierarchyPage() {
  const user = useAuth((s) => s.user);
  const [root, setRoot] = useState<Node | null>(null);
  const [tree, setTree] = useState<Record<string, Node[]>>({});
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState<Set<string>>(new Set());

  const load = useCallback(async (rootId?: string) => {
    const qs = rootId ? `?rootUserId=${rootId}` : '';
    const res = await api<Response>(`/wallet/hierarchy${qs}`);
    if (!rootId) setRoot(res.root);
    setTree((t) => ({ ...t, [rootId ?? res.root.id]: res.children }));
  }, []);

  useEffect(() => { if (user) load().catch(() => { /* */ }); }, [user, load]);

  async function toggle(nodeId: string) {
    if (expanded.has(nodeId)) {
      setExpanded((s) => { const n = new Set(s); n.delete(nodeId); return n; });
      return;
    }
    setExpanded((s) => new Set(s).add(nodeId));
    if (!tree[nodeId]) {
      setLoading((s) => new Set(s).add(nodeId));
      try { await load(nodeId); }
      finally {
        setLoading((s) => { const n = new Set(s); n.delete(nodeId); return n; });
      }
    }
  }

  return (
    <>
      <TopBar title="My Team" />
      <div className="px-4 pt-4 space-y-4">
        {root && (
          <Card glow>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-accent/20 border border-accent/40 flex items-center justify-center text-accent">
                <UserIcon size={22} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <div className="font-bold truncate">{root.username}</div>
                  <Badge tone="accent">{root.vRank}</Badge>
                </div>
                <div className="text-[11px] text-fg-muted font-mono">Ref: {root.referralCode}</div>
              </div>
            </div>
            <div className="mt-3 grid grid-cols-3 gap-2">
              <StatCard label="Gen 1" value={root.childCount} />
              <StatCard label="Items" value={fmt(root.personalItems, 0)} />
              <StatCard label="Earned" value={fmt(root.totalBonusUsdt)} suffix="U" accent="success" />
            </div>
          </Card>
        )}

        <Card padded={false}>
          <div className="p-4 pb-2 text-sm font-bold">Downlines</div>
          {!root ? (
            <div className="p-6 text-center text-sm text-fg-muted">Loading…</div>
          ) : (tree[root.id] ?? []).length === 0 ? (
            <div className="p-6 text-center text-sm text-fg-muted">
              No direct referrals yet. Share your code to grow.
            </div>
          ) : (
            <ul className="divide-y divide-border">
              {(tree[root.id] ?? []).map((child) => (
                <TreeRow
                  key={child.id}
                  node={child}
                  depth={0}
                  tree={tree}
                  expanded={expanded}
                  loading={loading}
                  onToggle={toggle}
                />
              ))}
            </ul>
          )}
        </Card>
      </div>
    </>
  );
}

function TreeRow({
  node, depth, tree, expanded, loading, onToggle,
}: {
  node: Node; depth: number;
  tree: Record<string, Node[]>;
  expanded: Set<string>;
  loading: Set<string>;
  onToggle: (id: string) => void;
}) {
  const isOpen = expanded.has(node.id);
  const kids = tree[node.id] ?? [];
  const canExpand = node.childCount > 0;

  return (
    <>
      <li
        className="p-3 flex items-center gap-2"
        style={{ paddingLeft: 12 + depth * 16 }}
      >
        <button
          onClick={() => canExpand && onToggle(node.id)}
          className={`w-6 h-6 rounded-md flex items-center justify-center ${canExpand ? 'text-fg-muted hover:text-fg hover:bg-surface-2' : 'text-fg-subtle'}`}
          disabled={!canExpand}
        >
          {canExpand ? (isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />) : <span className="w-1 h-1 rounded-full bg-fg-subtle" />}
        </button>
        <div className="flex-1 min-w-0 flex items-center gap-2">
          <div className="text-sm font-semibold truncate">{node.username}</div>
          <Badge>{node.vRank}</Badge>
        </div>
        <div className="text-right text-[11px] font-mono">
          <div className="text-fg">{fmt(node.personalItems, 0)} it</div>
          <div className="text-success">+{fmt(node.totalBonusUsdt)}</div>
        </div>
      </li>
      {isOpen && (
        <>
          {loading.has(node.id) && <li className="p-3 pl-12 text-xs text-fg-muted">Loading…</li>}
          {kids.map((k) => (
            <TreeRow
              key={k.id}
              node={k}
              depth={depth + 1}
              tree={tree}
              expanded={expanded}
              loading={loading}
              onToggle={onToggle}
            />
          ))}
        </>
      )}
    </>
  );
}
